import * as React from 'react';
import { Grid, List, ListItem, ListItemText, Divider, Typography, 
    CircularProgress, Button, Box } from '@mui/material';
import { GridFormItem } from '../../../GridForm';
import { Patient } from '../../../../models/core';
import { API } from '../../../../utils/Api';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { HistoryListItem } from './HistoryListItem';

interface HistoryViewProps {
    patient:Patient;
}

function useGetsRosters(patient:Patient) {
    return useQuery({
        queryKey: ['patient.rosters', patient.clientId, patient._id],
        queryFn: async () => (await API.getRosters(patient.clientId, patient._id)),
        select: (data) => data,
        staleTime:240_000
    });
}

function useGetHistory(patient:Patient) {
    return useQuery({
        queryKey: ['patient.history', patient.clientId, patient._id],
        queryFn: async () => (await API.getHistoryList(patient.clientId, undefined, patient._id)),
        select: (data) => data,
        staleTime:240_000,
        enabled:false
    });
}

const HistoryView = ({ patient }: HistoryViewProps): JSX.Element => {
    const rosterQuery = useGetsRosters(patient);
    const historyQuery = useGetHistory(patient);
    const rosters = React.useMemo(() => { return rosterQuery.data?.items || [] }, [rosterQuery]);
    const history = React.useMemo(() => { return historyQuery.data?.items.sort(
        (a, b) => (DateTime.fromISO(a.updated || '') > DateTime.fromISO(b.updated || "")) ? 1 : -1 
    ) || [] }, [rosterQuery]);
    return <Grid container spacing={2}>
        <GridFormItem xs={12}><Typography variant='h5'>Sources</Typography></GridFormItem>
        <GridFormItem xs={12}><Divider/></GridFormItem>
        <GridFormItem xs={12}>
            <Typography variant='h6'>Import Rosters</Typography>
        </GridFormItem>
        <GridFormItem xs={12} height='30%'>
            <Box border={1} padding='3px'>
                <List>
                    {
                        rosterQuery.isFetching || rosterQuery.isLoading
                        ?
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </div>
                        :
                        (rosters && rosters.length > 0) ? rosters.map((r) => {
                            return (<ListItem key={r._id}>
                                <Grid container spacing={1}>
                                    <GridFormItem xs={12}>
                                        <Box display='flex' justifyContent='space-between'>
                                            <Typography component='div' sx={{ display: 'flex', justifyContent: 'start' }}>
                                                <b>{`${r.name}`}</b>
                                            </Typography>
                                            <Typography component='div' sx={{ display: 'flex', justifyContent: 'end' }}>
                                                {`${DateTime.fromISO(r.importDate).toISODate()}`}
                                            </Typography>
                                        </Box>
                                    </GridFormItem>
                                    <GridFormItem xs={12}>
                                        <Typography component='div' sx={{ display: 'flex', justifyContent: 'start' }}>
                                            {`${r.note}`}
                                        </Typography>
                                    </GridFormItem>
                                </Grid>
                                
                                <Divider />
                            </ListItem>)
                        })
                        :
                            <ListItem>
                                <Typography component='div' sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                                    No Rosters Found for Patient
                                </Typography>
                            </ListItem>
                    }
                </List>
            </Box>
        </GridFormItem>
        <GridFormItem xs={12}>
            <Typography variant="h6">Change History</Typography>
        </GridFormItem>
        <GridFormItem xs={12} height='60%'>
            <Box border={1} padding='2px'>
            <List>
                {historyQuery.isFetching || historyQuery.isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </div>
                )
                :
                (history && history.length > 0) ? history.map((r) => {
                    return (<ListItem key={r._id}>
                        <HistoryListItem historyItem={r}/>
                    </ListItem>)
                })
                :
                (
                    <ListItem>
                        <ListItemText primary="No history available" />
                    </ListItem>
                )}
            </List>
            </Box>
        </GridFormItem>

        <GridFormItem xs={12}>
            <Button variant="contained" onClick={()=>historyQuery.refetch()}>Retrieve History</Button>
        </GridFormItem>
    </Grid>
};
export default HistoryView;